import type { GameState } from './state';
import { createInitialState } from './state';
import type { Action } from './actions';
import { stepCombat } from './sim/combat';
import { levelFrame, levelFrameMax, fullSend, triggerMaxEffort, navWave } from './sim/progression';
import { equipPart, unequipPart, scrapPart, toggleProtect, upgradePart, moveNode, connect, disconnect } from './graph/ops';
import { ascend, reboot } from './sim/ascension';
import { buyCoreUpgrade } from './sim/meta';
import { startOverlordFight, devDefeatChallenge } from './sim/overlords';
import { equipPet, unequipPet, grantPet } from './sim/pets';
import { unlockDrones } from './sim/drones';
import { segmentStart } from './sim/combat';
import { PET_DEFS } from './data/pets';
import { stepDrones, launchSortie, collectSortie, repairDrones, buildDrone, upgradeBay } from './sim/drones';
import { startFusion, collectFusion, unlockFoundry } from './sim/foundry';
import { startTrial, collectTrials, upgradeYard, unlockYard } from './sim/yard';
import { stepSmelter, queueSmelt, smeltAllOfRarity, buySmeltUpgrade, unlockSmelter, drainSmelter } from './sim/smelter';
import { stepRails, layRoute, repairRoute, unlockRails } from './sim/rails';
import { stepTakeover, buyStake, shiftStake, unlockTakeover } from './sim/takeover';
import { stepGrid, startIntrusion, buyGridUpgrade, unlockGrid } from './sim/grid';
import { assignPop, unassignPop, unlockDistricts } from './sim/districts';
import { stepHarvest, launchSatellite, unlockHarvest } from './sim/harvest';
import { add, D } from './math/num';
import type { CombatEvent, EmitFn } from './sim/events';

/** Fixed timestep: 10 ticks/sec. The host accumulates real time and calls
 *  tick(dt) in fixed increments; the core never reads the wall clock. */
export const TICK_MS = 100;

/** Pure simulation step. Exported so offline replay reuses the exact path.
 *  `emit` is optional — omitted during offline replay so no events are produced. */
export function stepWorld(state: GameState, dtMs: number, emit?: EmitFn): void {
  state.elapsedMs += dtMs;
  stepCombat(state, dtMs, emit);
  stepDrones(state, dtMs);
  stepSmelter(state, dtMs);
  stepRails(state, dtMs);
  stepTakeover(state, dtMs);
  stepGrid(state, dtMs);
  stepHarvest(state, dtMs);
}

/** Apply a UI action to the state. The single mutation entry point for actions. */
export function applyAction(state: GameState, action: Action, emit?: EmitFn): void {
  switch (action.type) {
    case 'SET_NOTATION':
      state.settings.notation = action.notation;
      break;
    case 'SET_REDUCED_MOTION':
      state.settings.reducedMotion = action.value;
      break;
    case 'LEVEL_FRAME':
      levelFrame(state);
      break;
    case 'LEVEL_FRAME_MAX':
      levelFrameMax(state);
      break;
    case 'FULL_SEND':
      fullSend(state, emit);
      break;
    case 'MAX_EFFORT':
      triggerMaxEffort(state);
      break;
    case 'NAV_WAVE':
      navWave(state, action.dir);
      break;
    case 'EQUIP_PART':
      equipPart(state, action.instanceId, action.x, action.y);
      break;
    case 'UNEQUIP_PART':
      unequipPart(state, action.instanceId);
      break;
    case 'SCRAP_PART':
      scrapPart(state, action.instanceId);
      break;
    case 'SCRAP_PARTS':
      for (const id of action.instanceIds) scrapPart(state, id);
      break;
    case 'PROTECT_PART':
      toggleProtect(state, action.instanceId);
      break;
    case 'SET_AUTO_ADVANCE':
      state.combat.autoAdvance = action.on;
      break;
    case 'UPGRADE_PART':
      upgradePart(state, action.instanceId);
      break;
    case 'MOVE_NODE':
      moveNode(state, action.nodeId, action.x, action.y);
      break;
    case 'CONNECT':
      connect(state, action.fromNode, action.fromPort, action.toNode, action.toPort);
      break;
    case 'DISCONNECT':
      disconnect(state, action.edgeId);
      break;
    case 'ASCEND':
      ascend(state);
      break;
    case 'REBOOT':
      reboot(state);
      break;
    case 'BUY_CORE_UPGRADE':
      buyCoreUpgrade(state, action.id);
      break;
    case 'START_OVERLORD':
      startOverlordFight(state, action.id, emit);
      break;
    case 'EQUIP_PET':
      equipPet(state, action.modelId);
      break;
    case 'UNEQUIP_PET':
      unequipPet(state, action.modelId);
      break;
    case 'LAUNCH_SORTIE':
      launchSortie(state, action.tier, action.drones);
      break;
    case 'COLLECT_SORTIE':
      collectSortie(state);
      break;
    case 'REPAIR_DRONES':
      repairDrones(state);
      break;
    case 'BUILD_DRONE':
      buildDrone(state);
      break;
    case 'UPGRADE_BAY':
      upgradeBay(state);
      break;
    case 'START_FUSION':
      startFusion(state, action.defId, action.rarity);
      break;
    case 'COLLECT_FUSION':
      collectFusion(state, action.hot, emit);
      break;
    case 'START_TRIAL':
      startTrial(state, action.petId, action.trialId);
      break;
    case 'COLLECT_TRIALS':
      collectTrials(state);
      break;
    case 'UPGRADE_YARD':
      upgradeYard(state);
      break;
    case 'SMELT_PARTS':
      queueSmelt(state, action.instanceIds);
      break;
    case 'SMELT_RARITY':
      smeltAllOfRarity(state, action.rarity);
      break;
    case 'BUY_SMELT_UPGRADE':
      buySmeltUpgrade(state, action.id);
      break;
    case 'LAY_ROUTE':
      layRoute(state, action.a, action.b);
      break;
    case 'REPAIR_ROUTE':
      repairRoute(state, action.a, action.b);
      break;
    case 'BUY_STAKE':
      buyStake(state, action.id);
      break;
    case 'SHIFT_STAKE':
      shiftStake(state, action.from, action.to);
      break;
    case 'START_INTRUSION':
      startIntrusion(state, action.id);
      break;
    case 'BUY_GRID_UPGRADE':
      buyGridUpgrade(state, action.id);
      break;
    case 'ASSIGN_POP':
      assignPop(state, action.district, action.n);
      break;
    case 'UNASSIGN_POP':
      unassignPop(state, action.district, action.n);
      break;
    case 'LAUNCH_SATELLITE':
      launchSatellite(state);
      break;
    case 'DEV_ADD_SCRAP':
      state.wallet.scrap = add(state.wallet.scrap, D(action.amount));
      break;
    case 'DEV_ADD_COMPONENTS':
      state.wallet.components = add(state.wallet.components, D(action.amount));
      break;
    case 'DEV_ADD_CORES':
      state.meta.cores = add(state.meta.cores, D(action.amount));
      break;
    case 'DEV_SET_WAVE': {
      const w = Math.max(1, Math.floor(action.wave));
      state.combat.wave = w;
      state.combat.highestWave = Math.max(state.combat.highestWave, w);
      state.combat.checkpoint = segmentStart(w);
      state.combat.enemies = [];
      break;
    }
    case 'DEV_DEFEAT_OVERLORD':
      devDefeatChallenge(state);
      break;
    case 'DEV_UNLOCK_DRONES':
      unlockDrones(state);
      break;
    case 'DEV_UNLOCK_PETS':
      state.pets.unlocked = true;
      break;
    case 'DEV_GRANT_PETS':
      state.pets.unlocked = true;
      for (const id of Object.keys(PET_DEFS)) grantPet(state, id);
      break;
    case 'DEV_FINISH_SORTIE':
      if (state.drones.sortie?.status === 'running') state.drones.sortie.endsMs = state.elapsedMs;
      break;
    case 'DEV_UNLOCK_FOUNDRY':
      unlockFoundry(state);
      break;
    case 'DEV_FINISH_FORGE':
      if (state.foundry.job) state.foundry.job.endsMs = state.elapsedMs;
      break;
    case 'DEV_UNLOCK_YARD':
      unlockYard(state);
      break;
    case 'DEV_FINISH_TRIALS':
      for (const t of state.yard.trials) t.endsMs = state.elapsedMs;
      break;
    case 'DEV_UNLOCK_SMELTER':
      unlockSmelter(state);
      break;
    case 'DEV_FINISH_SMELT':
      drainSmelter(state);
      break;
    case 'DEV_UNLOCK_RAILS':
      unlockRails(state);
      break;
    case 'DEV_UNLOCK_TAKEOVER':
      unlockTakeover(state);
      break;
    case 'DEV_PAY_DIVIDEND':
      if (state.takeover.unlocked && state.takeover.nextPayoutMs > 0) {
        state.takeover.nextPayoutMs = state.elapsedMs;
        stepTakeover(state, 0);
      }
      break;
    case 'DEV_UNLOCK_GRID':
      unlockGrid(state);
      break;
    case 'DEV_FINISH_INTRUSION':
      if (state.grid.run) {
        state.grid.run.endsMs = state.elapsedMs;
        stepGrid(state, 0);
      }
      break;
    case 'DEV_UNLOCK_DISTRICTS':
      unlockDistricts(state);
      break;
    case 'DEV_GRANT_POP':
      if (state.districts.unlocked) state.districts.popBonus += 25;
      break;
    case 'DEV_UNLOCK_HARVEST':
      unlockHarvest(state);
      break;
    case 'DEV_FINISH_LAUNCH':
      if (state.harvest.launch) {
        state.harvest.launch.endsMs = state.elapsedMs;
        stepHarvest(state, 0);
      }
      break;
    default: {
      const _never: never = action;
      void _never;
    }
  }
}

type Listener = () => void;
type EventListener = (ev: CombatEvent) => void;

/**
 * The observable store. Holds the single GameState, exposes a tearing-free
 * snapshot token for useSyncExternalStore, orchestrates tick/dispatch, and
 * forwards ephemeral combat events to render-layer listeners.
 */
export class Engine {
  private state: GameState;
  private listeners = new Set<Listener>();
  private eventListeners = new Set<EventListener>();
  private snapshotVersion = 0;

  constructor(initial?: GameState) {
    this.state = initial ?? createInitialState();
  }

  getState(): GameState {
    return this.state;
  }

  setState(next: GameState): void {
    this.state = next;
    this.notify();
  }

  getSnapshot = (): number => this.snapshotVersion;

  subscribe = (cb: Listener): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  /** Subscribe to ephemeral combat events (FX/floating text). */
  subscribeEvents = (cb: EventListener): (() => void) => {
    this.eventListeners.add(cb);
    return () => this.eventListeners.delete(cb);
  };

  private emit: EmitFn = (ev) => {
    for (const cb of this.eventListeners) cb(ev);
  };

  tick(dtMs: number): void {
    if (dtMs <= 0) return;
    stepWorld(this.state, dtMs, this.emit);
    this.notify();
  }

  dispatch = (action: Action): void => {
    applyAction(this.state, action, this.emit);
    this.notify();
  };

  private notify(): void {
    this.snapshotVersion++;
    for (const cb of this.listeners) cb();
  }
}
